'use client';

import React, { useEffect, useState } from 'react';
import { Mail, Phone, PauseCircle } from 'lucide-react';
import projectServices from '@/utils/services/project';

// Tên biến setting (nhóm Noti) của dự án; thiếu biến nào thì dùng giá trị mặc định tương ứng.
const SETTING_KEYS = {
    message: 'suspended_message',
    email: 'suspended_contact_email',
    phone: 'suspended_contact_phone',
    logo: 'suspended_logo',
} as const;

const DEFAULT_NOTICE = {
    message: 'Website đang tạm ngưng do dịch vụ chưa được gia hạn.',
    email: 'contact@starglobal3d.com',
    phone: '0944 850 527',
    logo: '/images/logo.png',
};

type NoticeContent = typeof DEFAULT_NOTICE;

interface ProjectSuspendedNoticeProps {
    idPath?: string;
    sublink?: string;
}

const NOTICE_SETTING_CATEGORY = 'Noti';

function pickSettingText(settings: Record<string, unknown>, key: string): string | null {
    const raw = settings[key];
    const value = raw && typeof raw === 'object' ? (raw as { value?: unknown }).value : raw;
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

const buttonBase =
    'inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-2.5 rounded-xl px-5 py-3 text-base font-semibold ' +
    'transition-all duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 active:scale-[0.98] ' +
    'motion-reduce:transition-none motion-reduce:active:scale-100 sm:w-auto';
const primaryButton = `${buttonBase} bg-blue-600 text-white shadow-lg shadow-blue-600/25 hover:bg-blue-700`;
const secondaryButton = `${buttonBase} border border-slate-300 bg-white text-slate-800 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700`;

/**
 * Full-screen notice shown instead of the project when its service
 * has not been renewed (full-path API returns active = 0).
 */
export default function ProjectSuspendedNotice({ idPath, sublink = '1' }: ProjectSuspendedNoticeProps) {
    const [content, setContent] = useState<NoticeContent | null>(idPath ? null : DEFAULT_NOTICE);

    useEffect(() => {
        if (!idPath) {
            setContent(DEFAULT_NOTICE);
            return;
        }
        let cancelled = false;
        (async () => {
            let settings: Record<string, unknown> = {};
            try {
                settings = await projectServices.getSettingsByCategory({ id_path: idPath, sublink, category: NOTICE_SETTING_CATEGORY });
            } catch (err) {
                console.warn('Unable to load suspended notice settings:', err);
            }
            if (cancelled) return;
            setContent({
                message: pickSettingText(settings, SETTING_KEYS.message) ?? DEFAULT_NOTICE.message,
                email: pickSettingText(settings, SETTING_KEYS.email) ?? DEFAULT_NOTICE.email,
                phone: pickSettingText(settings, SETTING_KEYS.phone) ?? DEFAULT_NOTICE.phone,
                logo: pickSettingText(settings, SETTING_KEYS.logo) ?? DEFAULT_NOTICE.logo,
            });
        })();
        return () => {
            cancelled = true;
        };
    }, [idPath, sublink]);

    // Chờ setting tải xong để tránh nháy nội dung mặc định rồi đổi sang nội dung đã cấu hình
    if (!content) return null;

    return (
        <main
            role="alert"
            className="fixed inset-0 z-[2147483647] flex items-center justify-center overflow-y-auto bg-gradient-to-br from-slate-50 via-blue-50 to-sky-100 p-4 text-slate-800 sm:p-6"
        >
            {/* Decorative blurred shapes, hidden from assistive tech */}
            <div aria-hidden="true" className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-blue-300/30 blur-3xl" />
            <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -right-24 h-80 w-80 rounded-full bg-sky-300/30 blur-3xl" />

            <section className="relative my-auto w-full max-w-xl rounded-3xl border border-white/80 bg-white/90 px-6 py-10 text-center shadow-2xl shadow-blue-900/10 backdrop-blur sm:px-12 sm:py-14">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={content.logo} alt="Logo" className="mx-auto h-12 w-auto max-w-[220px] object-contain" />

                <div className="mx-auto mt-8 flex h-20 w-20 items-center justify-center rounded-full bg-amber-50 ring-8 ring-amber-100/70">
                    <PauseCircle className="h-10 w-10 text-amber-600" strokeWidth={1.75} aria-hidden="true" />
                </div>

                <h1 className="mt-6 text-balance text-2xl font-bold leading-snug tracking-tight text-slate-900 sm:text-3xl">
                    {content.message}
                </h1>

                <div className="mx-auto my-8 flex items-center gap-4 text-sm font-medium uppercase tracking-wider text-slate-500">
                    <span className="h-px flex-1 bg-slate-200" />
                    Vui lòng liên hệ
                    <span className="h-px flex-1 bg-slate-200" />
                </div>

                <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row">
                    <a href={`mailto:${content.email}`} className={primaryButton}>
                        <Mail className="h-5 w-5 shrink-0" aria-hidden="true" />
                        {content.email}
                    </a>
                    <a href={`tel:${content.phone.replace(/\s/g, '')}`} className={secondaryButton}>
                        <Phone className="h-5 w-5 shrink-0" aria-hidden="true" />
                        {content.phone}
                    </a>
                </div>
            </section>
        </main>
    );
}
